import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import * as api from '../api';

export function usePersons(params?: Parameters<typeof api.fetchPersons>[0]) {
  return useQuery({
    queryKey: ['persons', params],
    queryFn: () => api.fetchPersons(params),
  });
}

export function usePerson(slug: string | null) {
  return useQuery({
    queryKey: ['person', slug],
    queryFn: () => api.fetchPerson(slug!),
    enabled: !!slug,
  });
}

export function usePersonConnections(slug: string | null, params?: { min_shared_docs?: number; limit?: number }) {
  return useQuery({
    queryKey: ['person-connections', slug, params],
    queryFn: () => api.fetchPersonConnections(slug!, params),
    enabled: !!slug,
  });
}

export function usePersonDocuments(slug: string | null, params?: { page?: number; per_page?: number }) {
  return useQuery({
    queryKey: ['person-documents', slug, params],
    queryFn: () => api.fetchPersonDocuments(slug!, params),
    enabled: !!slug,
  });
}

export function useDocuments(params?: Parameters<typeof api.fetchDocuments>[0]) {
  return useQuery({
    queryKey: ['documents', params],
    queryFn: () => api.fetchDocuments(params),
  });
}

export function useDocument(id: string | null) {
  return useQuery({
    queryKey: ['document', id],
    queryFn: () => api.fetchDocument(id!),
    enabled: !!id,
  });
}

export function useSearch(query: string, type?: string) {
  return useQuery({
    queryKey: ['search', query, type],
    queryFn: () => api.fetchSearch({ q: query, type, limit: 50 }),
    enabled: query.length >= 2,
  });
}

export function useFlights(params?: Parameters<typeof api.fetchFlights>[0]) {
  return useQuery({
    queryKey: ['flights', params],
    queryFn: () => api.fetchFlights(params),
  });
}

export function useNetworkGraph(minSharedDocs = 20) {
  return useQuery({
    queryKey: ['network-graph', minSharedDocs],
    queryFn: () => api.fetchNetworkGraph(minSharedDocs),
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useFinancialFlows(params?: Parameters<typeof api.fetchFinancialFlows>[0]) {
  return useQuery({
    queryKey: ['financial-flows', params],
    queryFn: () => api.fetchFinancialFlows(params),
  });
}

export function useFinancialStats() {
  return useQuery({
    queryKey: ['financial-stats'],
    queryFn: () => api.fetchFinancialStats(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useFinancialEntities(params?: { page?: number; per_page?: number }) {
  return useQuery({
    queryKey: ['financial-entities', params],
    queryFn: () => api.fetchFinancialEntities(params),
  });
}

export function useLocations() {
  return useQuery({
    queryKey: ['locations'],
    queryFn: () => api.fetchLocations(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useStats() {
  return useQuery({
    queryKey: ['stats'],
    queryFn: () => api.fetchStats(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useTimeline(params?: Parameters<typeof api.fetchTimeline>[0]) {
  return useQuery({
    queryKey: ['timeline', params],
    queryFn: () => api.fetchTimeline(params),
  });
}

export function useEmails(params: { q: string; page?: number; per_page?: number }) {
  return useQuery({
    queryKey: ['emails', params],
    queryFn: () => api.fetchEmails(params),
    enabled: params.q.length >= 2,
  });
}
